import { api } from './apiClient';
import {
  TreasuryDashboard, CashPosition, CashForecast, WorkingCapital, RiskAlerts,
  BankAccount, TreasuryPayment, Investment, Loan, LoanInstallment, Ageing,
  BudgetVsActual, TreasuryOverview, CashManagement, DebtFinancing, BankingOverview,
  PaymentsControl, ReconciliationOverview, InvestmentCenter, WorkingCapitalCenter, ArApCenter,
  ArOpenInvoice, RecordCollectionPayload, RecordCollectionResult,
} from '../types/treasury';

/**
 * Typed client for /api/v1/treasury. Mirrors erp-api/src/controllers/treasury.js.
 * apiClient unwraps the {status,data} envelope to `data`.
 */
const treasuryService = {
  // Dashboard / analytics
  getOverview: () => api.get<TreasuryOverview>('/treasury/overview'),
  getDashboard: () => api.get<TreasuryDashboard>('/treasury/dashboard'),
  getCashPosition: () => api.get<CashPosition>('/treasury/cash-position'),
  getBankBalances: () => api.get<{ accounts: BankAccount[] }>('/treasury/bank-balances'),
  getCashForecast: (horizon = 90) => api.get<CashForecast>(`/treasury/cash-forecast?horizon=${horizon}`),
  getCashManagement: (horizon = 30) => api.get<CashManagement>(`/treasury/cash-management?horizon=${horizon}`),
  setMinCash: (minCash: number) => api.put<{ minCash: number }>('/treasury/min-cash', { minCash }),
  getWorkingCapital: () => api.get<WorkingCapital>('/treasury/working-capital'),
  getRisk: () => api.get<RiskAlerts>('/treasury/risk'),
  getArApCenter: () => api.get<ArApCenter>('/treasury/ar-ap-center'),
  getReceivables: (asOnDate?: string) => api.get<Ageing>('/treasury/receivables', { params: { asOnDate } }),
  getPayables: (asOnDate?: string) => api.get<Ageing>('/treasury/payables', { params: { asOnDate } }),
  getCustomerOpenInvoices: (customerId: number) =>
    api.get<{ invoices: ArOpenInvoice[]; count: number }>(`/treasury/receivables/customers/${customerId}/invoices`),
  recordCollection: (invoiceId: number, payload: RecordCollectionPayload) =>
    api.post<RecordCollectionResult>(`/treasury/receivables/invoices/${invoiceId}/collect`, payload),

  // Bank accounts
  getBankingOverview: () => api.get<BankingOverview>('/treasury/banking-overview'),
  listBanks: () => api.get<{ accounts: BankAccount[] }>('/treasury/banks'),
  createBank: (data: Partial<BankAccount> & Record<string, any>) =>
    api.post<{ account: BankAccount }>('/treasury/banks', data),
  updateBank: (id: number, data: Record<string, any>) =>
    api.put<{ account: BankAccount }>(`/treasury/banks/${id}`, data),
  deactivateBank: (id: number) => api.delete<{ account: BankAccount }>(`/treasury/banks/${id}`),

  // Payments
  getPaymentsControl: () => api.get<PaymentsControl>('/treasury/payments-control'),
  listPayments: (params?: Record<string, any>) =>
    api.get<{ payments: TreasuryPayment[] }>('/treasury/payments', { params }),
  getPayment: (id: number) => api.get<{ payment: TreasuryPayment }>(`/treasury/payments/${id}`),
  createPayment: (data: Record<string, any>) =>
    api.post<{ payment: TreasuryPayment }>('/treasury/payments', data),
  submitPayment: (id: number) => api.post<{ payment: TreasuryPayment }>(`/treasury/payments/${id}/submit`),
  approvePayment: (id: number, action: 'APPROVE' | 'REJECT', comments?: string) =>
    api.post<{ payment: TreasuryPayment }>(`/treasury/payments/${id}/approve`, { action, comments }),
  executePayment: (id: number) => api.post<{ payment: TreasuryPayment }>(`/treasury/payments/${id}/execute`),
  cancelPayment: (id: number) => api.post<{ payment: TreasuryPayment }>(`/treasury/payments/${id}/cancel`),

  // Investments
  getInvestmentCenter: () => api.get<InvestmentCenter>('/treasury/investment-center'),
  listInvestments: (params?: Record<string, any>) =>
    api.get<{ investments: Investment[] }>('/treasury/investments', { params }),
  createInvestment: (data: Record<string, any>) =>
    api.post<{ investment: Investment }>('/treasury/investments', data),
  redeemInvestment: (id: number, data: Record<string, any>) =>
    api.post<{ investment: Investment }>(`/treasury/investments/${id}/redeem`, data),

  // Loans
  listLoans: (params?: Record<string, any>) => api.get<{ loans: Loan[] }>('/treasury/loans', { params }),
  getDebtFinancing: () => api.get<DebtFinancing>('/treasury/debt-financing'),
  getLoan: (id: number) => api.get<{ loan: Loan; schedule: LoanInstallment[] }>(`/treasury/loans/${id}`),
  createLoan: (data: Record<string, any>) => api.post<{ loan: Loan }>('/treasury/loans', data),
  recordEmi: (id: number) => api.post<{ loan: Loan }>(`/treasury/loans/${id}/emi`),

  // Budget & FX
  getWorkingCapitalCenter: () => api.get<WorkingCapitalCenter>('/treasury/working-capital-center'),
  getBudget: () => api.get<BudgetVsActual>('/treasury/budget'),
  createBudget: (data: Record<string, any>) => api.post<{ id: number }>('/treasury/budget', data),
  getAccounts: () => api.get<{ accounts: Array<{ id: number; account_code: string; account_name: string; account_type: string }> }>('/treasury/accounts'),
  listFxRates: () => api.get<{ rates: any[] }>('/treasury/fx-rates'),
  createFxRate: (data: Record<string, any>) => api.post('/treasury/fx-rates', data),
  refreshFxRates: (force = false) =>
    api.post<{ ok: boolean; source: string; asOf?: string; updated?: number; message?: string; rates: any[] }>('/treasury/fx-rates/refresh', { force }),

  // Payment runs / NEFT-RTGS file export
  exportPayments: (bankAccountId?: number) =>
    api.post<{ runNumber: string; filename: string; content: string; count: number; totalAmount: number }>('/treasury/payments/export', { bankAccountId }),
  listPaymentRuns: () => api.get<{ runs: any[] }>('/treasury/payment-runs'),

  // Bank reconciliation
  getReconciliationOverview: () => api.get<ReconciliationOverview>('/treasury/reconciliation-overview'),
  listReconciliations: () => api.get<{ reconciliations: any[] }>('/treasury/reconciliations'),
  createReconciliation: (data: { bankAccountId: number; statementDate: string; statementBalance: number }) =>
    api.post<{ reconciliation: any }>('/treasury/reconciliations', data),
  getReconciliation: (id: number) => api.get<{ reconciliation: any }>(`/treasury/reconciliations/${id}`),
  reconcileItem: (id: number, itemId: number, bankReference?: string) =>
    api.patch<{ reconciliation: any }>(`/treasury/reconciliations/${id}/items/${itemId}`, { bankReference }),
  completeReconciliation: (id: number) => api.post<{ reconciliation: any }>(`/treasury/reconciliations/${id}/complete`),
};

export default treasuryService;
